import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const IntegrationPanel = ({ config, onConfigChange }) => {
  const [activeTab, setActiveTab] = useState('customize');
  const [copied, setCopied] = useState(false);

  const embedCode = `<iframe
  src="https://pay.boltpay.io/widget?merchant=${config?.merchantId || 'YOUR_MERCHANT_ID'}"
  width="400"
  height="600"
  frameborder="0"
  style="border-radius: 12px;"
></iframe>`;

  const apiExample = `// Initialize BOLTPay Widget
const widget = new BOLTPay({
  merchantId: '${config?.merchantId || 'YOUR_MERCHANT_ID'}',
  apiKey: 'YOUR_API_KEY',
  theme: '${config?.theme}',
  brandColor: '${config?.brandColor}',
  onSuccess: (transaction) => {
    console.log('Payment successful:', transaction);
  },
  onError: (error) => {
    console.error('Payment failed:', error);
  }
});

widget.mount('#payment-container');`;

  const handleCopy = (text) => {
    navigator?.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tabs = [
    { id: 'customize', label: 'Customize', icon: 'Palette' },
    { id: 'embed', label: 'Embed Code', icon: 'Code' },
    { id: 'api', label: 'API Docs', icon: 'Book' },
    { id: 'webhooks', label: 'Webhooks', icon: 'Webhook' }
  ];

  const themeOptions = [
    { value: 'light', label: 'Light Theme' },
    { value: 'dark', label: 'Dark Theme' },
    { value: 'auto', label: 'Auto (System)' }
  ];

  return (
    <div className="card p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground mb-1">Integration</h2>
        <p className="text-sm text-muted-foreground">Configure and embed your payment widget</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 bg-muted/30 rounded-lg">
        {tabs?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab?.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={14} />
            <span className="hidden sm:inline">{tab?.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-4">
        {/* Customize Tab */}
        {activeTab === 'customize' && (
          <div className="space-y-4">
            <Input
              label="Merchant Name"
              value={config?.merchantName}
              onChange={(e) => onConfigChange({ ...config, merchantName: e?.target?.value })}
              placeholder="Your Business Name"
            />

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Brand Color
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={config?.brandColor}
                  onChange={(e) => onConfigChange({ ...config, brandColor: e?.target?.value })}
                  className="w-12 h-10 rounded border border-border cursor-pointer"
                />
                <Input
                  value={config?.brandColor}
                  onChange={(e) => onConfigChange({ ...config, brandColor: e?.target?.value })}
                  placeholder="#3B82F6"
                  className="flex-1"
                />
              </div>
            </div>

            <Select
              label="Theme"
              options={themeOptions}
              value={config?.theme}
              onChange={(value) => onConfigChange({ ...config, theme: value })}
            />

            <div className="space-y-3 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Input
                  type="checkbox"
                  checked={config?.enableQR}
                  onChange={(e) => onConfigChange({ ...config, enableQR: e?.target?.checked })}
                />
                <span className="text-sm text-foreground">Enable QR Code Payments</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <Input
                  type="checkbox"
                  checked={config?.enableRateLock}
                  onChange={(e) => onConfigChange({ ...config, enableRateLock: e?.target?.checked })}
                />
                <span className="text-sm text-foreground">Enable Rate Lock (10 min)</span>
              </label>
            </div>

            <Button variant="outline" size="sm" fullWidth iconName="Eye">
              Preview Widget
            </Button>
          </div>
        )}

        {/* Embed Code Tab */}
        {activeTab === 'embed' && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-foreground">Embed Code</label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(embedCode)}
                  iconName={copied ? 'Check' : 'Copy'}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <pre className="p-4 bg-muted rounded-lg text-xs font-mono text-foreground overflow-x-auto">
                {embedCode}
              </pre>
            </div>

            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-primary mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">Integration Steps</p>
                  <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Copy the embed code above</li>
                    <li>Paste it into your website HTML</li>
                    <li>Replace YOUR_MERCHANT_ID with your actual ID</li>
                    <li>Test in sandbox mode before going live</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* API Docs Tab */}
        {activeTab === 'api' && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-foreground">JavaScript SDK</label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(apiExample)}
                  iconName={copied ? 'Check' : 'Copy'}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <pre className="p-4 bg-muted rounded-lg text-xs font-mono text-foreground overflow-x-auto">
                {apiExample}
              </pre>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-muted/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Key" size={14} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">API Key</span>
                </div>
                <code className="text-xs font-mono text-muted-foreground">sk_live_xxxxxxxxxxxxx</code>
              </div>

              <div className="p-3 bg-muted/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Globe" size={14} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">API Endpoint</span>
                </div>
                <code className="text-xs font-mono text-muted-foreground">https://api.boltpay.io/v1</code>
              </div>
            </div>

            <Button variant="outline" size="sm" fullWidth iconName="ExternalLink">
              View Full Documentation
            </Button>
          </div>
        )}

        {/* Webhooks Tab */}
        {activeTab === 'webhooks' && (
          <div className="space-y-4">
            <Input
              label="Webhook URL"
              placeholder="https://your-site.com/webhooks/boltpay"
              description="Receive real-time payment notifications"
            />

            <div>
              <label className="text-sm font-medium text-foreground mb-3 block">Event Types</label>
              <div className="space-y-2">
                {[
                  { id: 'payment.success', label: 'Payment Successful' },
                  { id: 'payment.failed', label: 'Payment Failed' },
                  { id: 'payment.pending', label: 'Payment Pending' },
                  { id: 'refund.processed', label: 'Refund Processed' }
                ]?.map(event => (
                  <label key={event?.id} className="flex items-center gap-2 cursor-pointer">
                    <Input type="checkbox" defaultChecked />
                    <span className="text-sm text-foreground">{event?.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">Webhook Security</p>
                  <p className="text-xs text-muted-foreground">
                    All webhook requests are signed with HMAC-SHA256. Verify signatures to ensure authenticity.
                  </p>
                </div>
              </div>
            </div>

            <Button variant="default" size="sm" fullWidth iconName="Send">
              Test Webhook
            </Button>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="mt-6 pt-6 border-t border-border">
        <h4 className="text-sm font-semibold text-foreground mb-3">Integration Status</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-muted/20 rounded-lg">
            <div className="text-xs text-muted-foreground mb-1">Mode</div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-warning"></div>
              <span className="text-sm font-medium text-foreground">Sandbox</span>
            </div>
          </div>
          <div className="p-3 bg-muted/20 rounded-lg">
            <div className="text-xs text-muted-foreground mb-1">Status</div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-success"></div>
              <span className="text-sm font-medium text-foreground">Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationPanel;